import Button from '@mui/material/Button';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApplicationModal } from '@/components/organisms/ApplicationModal';
import { useTransactionId } from '@/hooks/useTransactionId';
import { useTransactionNotification } from '@/hooks/useTransactionNotification';
import { useTranslator } from '@/hooks/useTranslator';
import { IntegratorOperation, StatusType, TransactionNotification } from '@/types/transaction';

export const CustomerDepositPending: React.FC = (): React.ReactElement => {
  const tr = useTranslator();
  const transactionId = useTransactionId();
  const notifications = useTransactionNotification(transactionId);
  const navigate = useNavigate();
  const { customerId = '' } = useParams<{ readonly customerId: string }>();

  const [operation, setOperation] = React.useState<string>('');

  const onClose = React.useCallback((): void => {
    navigate(`/customers/${customerId}`, { replace: true });
  }, [navigate, customerId]);

  React.useEffect((): void => {
    const completed = notifications[notifications.length - 1];
    if (completed?.status === StatusType.SUCCEEDED) {
      navigate(`/customers/${customerId}/deposit/completed`, { replace: true });
      return;
    }

    const current = notifications
      .slice()
      .reverse()
      .find(
        (notification: TransactionNotification): boolean =>
          notification.status === StatusType.SUCCEEDED,
      );

    if (current) {
      const { operation } = current;

      setOperation(friendlyOperationNames[operation] ?? '');
    }
  }, [navigate, notifications, customerId]);

  const description = React.useMemo(
    (): string => tr('DEPOSIT_PENDING_DESCRIPTION', operation),
    [operation, tr],
  );

  return (
    <ApplicationModal
      title={tr('WE_ARE_DEPOSITING_YOUR_FUNDS')}
      description={description}
      onClose={onClose}>
      <Button variant="dark" onClick={onClose} fullWidth={true}>
        {tr('CLOSE')}
      </Button>
    </ApplicationModal>
  );
};

const friendlyOperationNames: Partial<{ [key in IntegratorOperation]: string }> = {
  [IntegratorOperation.ON_RAMP_DEPOSIT_RECEIVED]: 'on ramp deposit',
  [IntegratorOperation.PIX_DEPOSIT_RECEIVED]: 'PIX deposit',
  [IntegratorOperation.WIRE_DEPOSIT_RECEIVED]: 'wire deposit',
  [IntegratorOperation.SPEI_DEPOSIT_RECEIVED]: 'SPEI deposit',
  [IntegratorOperation.CRYPTO_DEPOSIT_RECEIVED]: 'crypto deposit',
};
